import { useState, useEffect } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Restaurant, OpeningHour } from "@/lib/types";
import { queryClient, safeParseResponse } from "@/lib/queryClient";
import { queryKeys, invalidateAllRestaurantLists, invalidateRestaurantDetail } from "@/lib/queryKeys";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Save, Check, Truck, Clock, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";

interface RestaurantFormDeliveryProps {
  restaurant: Restaurant;
  onDirtyChange: (isDirty: boolean) => void;
  onSavingChange?: (isSaving: boolean) => void;
}

// Wochentage für die Anzeige
const WEEKDAYS = [
  "Montag", "Dienstag", "Mittwoch", "Donnerstag", 
  "Freitag", "Samstag", "Sonntag"
];

// Typ für Lieferzeiten
interface DeliveryHour {
  day: number;
  isOpen: boolean;
  start: string;
  end: string;
}

// Rückgabetyp der API für bessere Typsicherheit
interface DeliverySettingsApiResponse {
  deliveryHours?: OpeningHour[];
  data?: OpeningHour[];
  message?: string;
  error?: string;
  restaurant?: {
    id: string;
    delivery: boolean;
    [key: string]: any;
  };
  success?: boolean;
}

const RestaurantFormDelivery = ({ restaurant, onDirtyChange, onSavingChange: _onSavingChange }: RestaurantFormDeliveryProps) => {
  const { toast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("settings");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // P0 FIX: Track ob Lieferzeiten-Tab bereits initial geladen wurde
  const [hasLoadedHoursTab, setHasLoadedHoursTab] = useState(false);
  
  // Formular-Daten
  const [formData, setFormData] = useState({
    delivery: restaurant?.delivery ?? false,
    minimumOrderValue: restaurant?.minimumOrderValue ?? 15,
    maximumDeliveryRadius: restaurant?.maximumDeliveryRadius ?? 5,
    deliveryFee: restaurant?.deliveryFee ?? 2.50,
    freeDeliveryThreshold: restaurant?.freeDeliveryThreshold ?? 30,
    deliveryEstimate: restaurant?.deliveryEstimate ?? "30-45",
    deliveryInstructions: restaurant?.deliveryInstructions ?? ""
  });

  // P0: Direkter Zugriff auf aktuelle Lieferzeiten vom Server
  // WICHTIG: enabled nur wenn id existiert und nicht "new" ist
  const { refetch: refetchHours } = useQuery<OpeningHour[]>({
    queryKey: queryKeys.restaurants.deliveryHours(restaurant.id),
    initialData: restaurant.deliveryHours || [],
    enabled: Boolean(restaurant?.id && restaurant.id !== 'new'),
    staleTime: 0, // Keine Caching-Zeit, immer neu laden
    gcTime: 0, // Keine Cache-Aufbewahrung (ersetzt das veraltete cacheTime)
    refetchOnMount: true, // Immer neu laden, wenn die Komponente gemountet wird
    refetchOnWindowFocus: true, // Neu laden, wenn das Fenster den Fokus erhält
  });

  // Lieferzeiten
  const [deliveryHours, setDeliveryHours] = useState<DeliveryHour[]>(() => {
    // Aus bestehenden Daten laden oder Standardwerte setzen
    if (restaurant?.deliveryHours && restaurant.deliveryHours.length > 0) {
      // Erstelle Map für einfacheren Zugriff nach Wochentag
      const hoursByDay = new Map<number, OpeningHour>();
      restaurant.deliveryHours.forEach(hour => {
        const day = hour.weekday || hour.day || 0;
        if (day > 0) {
          hoursByDay.set(day, hour);
        }
      });

      return WEEKDAYS.map((_, index) => {
        const day = index + 1;
        const existingHour = hoursByDay.get(day);
        
        if (existingHour) {
          return {
            day,
            isOpen: true,
            start: existingHour.openStart || existingHour.opening || "12:00",
            end: existingHour.openEnd || existingHour.closing || "21:00"
          };
        } else {
          return {
            day,
            isOpen: false,
            start: "12:00",
            end: "21:00"
          };
        }
      });
    } else {
      // Default-Werte
      return WEEKDAYS.map((_, index) => ({
        day: index + 1,
        isOpen: false,
        start: "12:00",
        end: "21:00"
      }));
    }
  });

  // Aktualisiere isDirty-Status
  useEffect(() => {
    // Prüfe, ob sich Daten geändert haben
    const hasFormChanges = 
      formData.delivery !== (restaurant?.delivery ?? false) ||
      formData.minimumOrderValue !== (restaurant?.minimumOrderValue ?? 15) ||
      formData.maximumDeliveryRadius !== (restaurant?.maximumDeliveryRadius ?? 5) ||
      formData.deliveryFee !== (restaurant?.deliveryFee ?? 2.50) ||
      formData.freeDeliveryThreshold !== (restaurant?.freeDeliveryThreshold ?? 30) ||
      formData.deliveryEstimate !== (restaurant?.deliveryEstimate ?? "30-45") ||
      formData.deliveryInstructions !== (restaurant?.deliveryInstructions ?? "");
    
    // Prüfe, ob sich Lieferzeiten geändert haben
    const hasHoursChanges = checkForHoursChanges();
    
    onDirtyChange(hasFormChanges || hasHoursChanges);
  }, [formData, deliveryHours, restaurant, onDirtyChange]);

  // Prüft, ob Änderungen an den Lieferzeiten vorgenommen wurden
  const checkForHoursChanges = (): boolean => {
    // Wenn keine ursprünglichen Lieferzeiten vorhanden sind, aber wir haben welche im UI
    if (!restaurant?.deliveryHours || restaurant.deliveryHours.length === 0) {
      return deliveryHours.some(hour => hour.isOpen);
    }

    // Erstelle Map der ursprünglichen Lieferzeiten nach Tag
    const originalHoursByDay = new Map<number, OpeningHour>();
    restaurant.deliveryHours.forEach(hour => {
      const day = hour.weekday || hour.day || 0;
      if (day > 0) {
        originalHoursByDay.set(day, hour);
      }
    });

    // Mindestanzahl offener Tage zählen
    const originalOpenDays = Array.from(originalHoursByDay.values()).length;
    const currentOpenDays = deliveryHours.filter(hour => hour.isOpen).length;
    
    // Wenn die Anzahl der offenen Tage unterschiedlich ist, gibt es Änderungen
    if (originalOpenDays !== currentOpenDays) {
      return true;
    }

    // Vergleiche aktuelle mit ursprünglichen Lieferzeiten
    for (const hour of deliveryHours) {
      const originalHour = originalHoursByDay.get(hour.day);
      
      // Wenn der Tag jetzt offen ist, aber vorher nicht, oder umgekehrt
      if (hour.isOpen && !originalHour) return true;
      if (!hour.isOpen && originalHour) return true;
      
      if (hour.isOpen && originalHour) {
        // Vergleiche Öffnungs- und Schließzeiten
        const origStart = originalHour.openStart || originalHour.opening || "";
        const origEnd = originalHour.openEnd || originalHour.closing || "";
        
        if (hour.start !== origStart) return true;
        if (hour.end !== origEnd) return true;
      }
    }
    
    return false;
  };

  // Mutation zum Aktualisieren der Lieferdaten
  const updateDeliverySettingsMutation = useMutation<DeliverySettingsApiResponse, Error, any>({
    mutationFn: async (data: any) => {
      console.log("Sende Daten an den Server:", data);
      
      try {
        const response = await fetch(`/api/restaurants/${restaurant.id}/delivery-settings`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          },
          body: JSON.stringify(data),
          credentials: 'include'
        });
        
        if (!response.ok) {
          // P0: Sichere Error-Parsing
          const errorData = await safeParseResponse<{ error?: string; message?: string }>(response);
          const errorMessage = errorData?.error || errorData?.message || `HTTP error! status: ${response.status}`;
          throw new Error(errorMessage);
        }
        
        // P0: Sichere Response-Parsing
        const responseData = await safeParseResponse(response);
        if (!responseData) {
          throw new Error("Leere Antwort vom Server");
        }
        console.log("Antwort vom Server:", responseData);
        return responseData;
      } catch (error) {
        console.error("Fehler bei der Serveranfrage:", error);
        throw error;
      }
    },
    onSuccess: async (result) => {
      console.log("Erfolgreiche Antwort erhalten:", result);
      
      // Extrahiere die aktualisierten Restaurant-Daten und Lieferzeitendaten
      const updatedRestaurant = (result.restaurant || {}) as { delivery?: boolean; [key: string]: any };
      const deliveryHoursData = result.deliveryHours || result.data || [];
      
      console.log("Aktualisierte Restaurant-Daten:", updatedRestaurant);
      console.log("Aktualisierte Lieferzeiten:", deliveryHoursData);
      
      // Setze den lokalen Status direkt basierend auf dem zurückgegebenen Wert
      // Entscheidend: Vergleiche strikt mit true/false
      const newDeliveryStatus = updatedRestaurant.delivery === true;
      console.log(`Setze lokalen delivery-Status auf: ${newDeliveryStatus} (Typ: ${typeof newDeliveryStatus})`);
      
      // Aktualisiere den lokalen Zustand direkt
      setFormData(prev => {
        const newState = { 
          ...prev, 
          delivery: newDeliveryStatus
        };
        console.log("Neuer lokaler Formular-Status:", newState);
        return newState;
      });
      
      // Wenn keine Lieferzeiten vom Server zurückgegeben wurden, aber welche im UI definiert sind,
      // verwenden wir die aktuellen UI-Daten
      const finalDeliveryHoursData = deliveryHoursData.length === 0 && deliveryHours.some(h => h.isOpen) ? 
        deliveryHours
          .filter(hour => hour.isOpen)
          .map(hour => ({
            weekday: hour.day,
            openStart: hour.start,
            openEnd: hour.end,
            restaurantId: restaurant.id
          })) : 
        deliveryHoursData;
      
      console.log("Verwende für Cache-Update:", finalDeliveryHoursData);
      
      // Vollständige Aktualisierung des Caches
      await queryClient.invalidateQueries({ 
        predicate: (query) => {
          // Alle relevanten Cache-Einträge ungültig machen
          return query.queryKey.some(key => 
            typeof key === 'string' && (
              key.includes(`/api/restaurants/${restaurant.id}`) ||
              key.includes('/api/restaurants') ||
              key === '/api/restaurants'
            )
          );
        }
      });
      
      // Frische Daten direkt laden
      try {
        const response = await fetch(`/api/restaurants/${restaurant.id}?full=true&cacheBuster=${Date.now()}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const freshRestaurant = await safeParseResponse<{ delivery?: boolean; deliveryHours?: OpeningHour[]; [key: string]: any }>(response);
          if (!freshRestaurant) {
            throw new Error("Leere Antwort vom Server");
          }
          console.log("Neu geladene Restaurant-Daten:", freshRestaurant);
          
          // Explizit den delivery-Status mit striktem Vergleich prüfen
          const freshDeliveryStatus = freshRestaurant.delivery === true;
          console.log(`Frisch geladener Delivery-Status: ${freshDeliveryStatus} (Originalwert: ${freshRestaurant.delivery}, Typ: ${typeof freshRestaurant.delivery})`);
          
          // Korrektur: Verwende den vom Server explizit gesendeten Wert 
          // statt immer den lokalen Wert zu priorisieren
          const finalDeliveryStatus = updatedRestaurant.delivery === true;
          console.log(`Finaler Delivery-Status für Cache: ${finalDeliveryStatus}`);
          
          // Wir fügen die Lieferzeiten manuell hinzu, falls sie im Restaurant-Objekt fehlen
          const restaurantWithDeliveryHours = {
            ...freshRestaurant,
            delivery: finalDeliveryStatus, // Stelle sicher, dass der delivery-Status korrekt ist
            deliveryHours: (freshRestaurant.deliveryHours && freshRestaurant.deliveryHours.length > 0) ? 
              freshRestaurant.deliveryHours : finalDeliveryHoursData,
            minimumOrderValue: freshRestaurant.minimumOrderValue ?? formData.minimumOrderValue,
            maximumDeliveryRadius: freshRestaurant.maximumDeliveryRadius ?? formData.maximumDeliveryRadius,
            deliveryFee: freshRestaurant.deliveryFee ?? formData.deliveryFee,
            freeDeliveryThreshold: freshRestaurant.freeDeliveryThreshold ?? formData.freeDeliveryThreshold,
            deliveryEstimate: freshRestaurant.deliveryEstimate ?? formData.deliveryEstimate,
            deliveryInstructions: freshRestaurant.deliveryInstructions ?? formData.deliveryInstructions
          } as Restaurant;
          
          // Formular-Daten aktualisieren - Verwende den finalen Wert für delivery
          setFormData(prev => ({
            ...prev,
            delivery: finalDeliveryStatus,
            minimumOrderValue: restaurantWithDeliveryHours.minimumOrderValue ?? formData.minimumOrderValue,
            maximumDeliveryRadius: restaurantWithDeliveryHours.maximumDeliveryRadius ?? formData.maximumDeliveryRadius,
            deliveryFee: restaurantWithDeliveryHours.deliveryFee ?? formData.deliveryFee,
            freeDeliveryThreshold: restaurantWithDeliveryHours.freeDeliveryThreshold ?? formData.freeDeliveryThreshold,
            deliveryEstimate: restaurantWithDeliveryHours.deliveryEstimate ?? formData.deliveryEstimate,
            deliveryInstructions: restaurantWithDeliveryHours.deliveryInstructions ?? formData.deliveryInstructions
          }));
          
          // Setze die aktualisierten Daten direkt in den Cache
          // P0: Cache-Invalidierung für Detail-Seite (beide Query-Keys: id UND slug)
          queryClient.setQueryData([`/api/restaurants/${restaurant.id}`], restaurantWithDeliveryHours);
          
          // WICHTIG: Auch Detail-Cache via Slug invalidieren (RestaurantDetailPage nutzt by-slug)
          if (restaurant.slug) {
            queryClient.setQueryData([`/api/restaurants/by-slug/${restaurant.slug}`], restaurantWithDeliveryHours);
          }
          
          // P0: Selektive Invalidierung statt global
          queryClient.invalidateQueries({ 
            queryKey: [`/api/restaurants/${restaurant.id}`],
            exact: false // Invalidiert auch by-slug Varianten
          });
          if (restaurant.slug) {
            queryClient.invalidateQueries({ 
              queryKey: [`/api/restaurants/by-slug/${restaurant.slug}`]
            });
          }
          
          // ✅ Auch queryKeys-Factory-basierte Invalidierung für konsistente Cache-Keys
          invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: true });
          invalidateAllRestaurantLists(queryClient);
          
          // Auch die Daten in den Hauptlisten aktualisieren
          queryClient.setQueryData(['/api/restaurants'], (oldData: any) => {
            if (!oldData) return oldData;
            return oldData.map((r: any) => r.id === restaurant.id ? 
              { ...r, delivery: finalDeliveryStatus, deliveryHours: finalDeliveryHoursData } : r
            );
          });
          
          // Lieferzeiten direkt im Cache aktualisieren
          queryClient.setQueryData(
            [`/api/restaurants/${restaurant.id}/delivery-hours`], 
            finalDeliveryHoursData
          );
          
          // Lieferzeiten aktualisieren - falls keine vom Server, behalte aktuelle
          if (restaurantWithDeliveryHours.deliveryHours && 
              restaurantWithDeliveryHours.deliveryHours.length > 0) {
            updateDeliveryHoursFromRestaurant(restaurantWithDeliveryHours);
          }
        } else {
          console.error("Fehler beim erneuten Laden des Restaurants:", response.status);
          throw new Error(`Fehler beim Laden der aktualisierten Daten: ${response.status}`);
        }
      } catch (error) {
        console.error("Fehler beim Neuladen der Restaurant-Daten:", error);
        // Trotz des Fehlers eine Erfolgsbenachrichtigung anzeigen, da die Daten gespeichert wurden
      }
      
      toast({
        title: "Liefereinstellungen aktualisiert",
        description: "Die Liefereinstellungen wurden erfolgreich gespeichert.",
      });
      
      onDirtyChange(false);
    },
    onError: (error: Error) => {
      console.error("Fehler beim Speichern:", error);
      setError(`Fehler beim Speichern: ${error.message}`);
      toast({
        title: "Fehler beim Speichern",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Hilfsfunktion zum Aktualisieren der Lieferzeiten aus dem Restaurant-Objekt
  const updateDeliveryHoursFromRestaurant = (restaurantData: Restaurant) => {
    if (!restaurantData?.deliveryHours || restaurantData.deliveryHours.length === 0) {
      return;
    }
    
    // Erstelle Map für einfacheren Zugriff nach Wochentag
    const hoursByDay = new Map<number, OpeningHour>();
    restaurantData.deliveryHours.forEach(hour => {
      const day = hour.weekday || hour.day || 0;
      if (day > 0) {
        hoursByDay.set(day, hour);
      }
    });

    // Aktualisiere die Lieferzeiten-Daten
    setDeliveryHours(prev => 
      prev.map((hour, index) => {
        const day = index + 1;
        const existingHour = hoursByDay.get(day);
        
        if (existingHour) {
          return {
            day,
            isOpen: true,
            start: existingHour.openStart || existingHour.opening || "12:00",
            end: existingHour.openEnd || existingHour.closing || "21:00"
          };
        } else {
          return {
            ...hour,
            isOpen: false
          };
        }
      })
    );
  };

  // Aktualisiere Daten und Lieferzeiten wenn restaurant sich ändert
  useEffect(() => {
    // Formular-Daten aktualisieren
    setFormData({
      delivery: restaurant?.delivery ?? false,
      minimumOrderValue: restaurant?.minimumOrderValue ?? 15,
      maximumDeliveryRadius: restaurant?.maximumDeliveryRadius ?? 5,
      deliveryFee: restaurant?.deliveryFee ?? 2.50,
      freeDeliveryThreshold: restaurant?.freeDeliveryThreshold ?? 30,
      deliveryEstimate: restaurant?.deliveryEstimate ?? "30-45",
      deliveryInstructions: restaurant?.deliveryInstructions ?? ""
    });
    
    // Lieferzeiten aktualisieren
    if (restaurant?.deliveryHours) {
      updateDeliveryHoursFromRestaurant(restaurant);
    }
  }, [restaurant]);

  // P0 FIX: Effekt zum Laden der Lieferzeiten - NUR beim ersten Öffnen des Tabs
  // WICHTIG: Nicht bei jedem Tab-Wechsel neu laden, sonst gehen gespeicherte Daten verloren!
  useEffect(() => {
    if (activeTab === "hours" && restaurant?.id && !hasLoadedHoursTab) {
      console.log("Tab 'Lieferzeiten' zum ersten Mal aktiviert - lade initiale Daten...");
      setHasLoadedHoursTab(true);
      
      // Lade explizit frische Daten (nur beim ersten Mal!)
      refetchHours().then(result => {
        const loadedDeliveryHours = result.data || [];
        console.log("Initial geladene Lieferzeiten:", loadedDeliveryHours);
        
        // Wenn Lieferzeiten vorhanden sind, übernehme sie
        if (loadedDeliveryHours.length > 0) {
          updateDeliveryHoursFromRestaurant({
            ...restaurant,
            deliveryHours: loadedDeliveryHours,
            delivery: formData.delivery
          });
        } else if (formData.delivery === true && !deliveryHours.some(h => h.isOpen)) {
          // Wenn Lieferung aktiviert ist, aber keine Lieferzeiten vorhanden sind, 
          // aktiviere Standard-Lieferzeiten für Werktage
          console.log("Keine Lieferzeiten gefunden, aktiviere Standard-Werktags-Zeiten");
          const updatedHours = [...deliveryHours];
          
          // Wochentage 1-5 (Mo-Fr) auf aktiv setzen
          for (let i = 0; i < 5; i++) {
            updatedHours[i] = {
              ...updatedHours[i],
              isOpen: true,
              start: "12:00",
              end: "21:00"
            };
          }
          
          setDeliveryHours(updatedHours);
        }
      }).catch(error => {
        console.error("Fehler beim Laden der Lieferzeiten:", error);
      });
    }
  }, [activeTab, restaurant?.id, hasLoadedHoursTab]);

  // Handler für den Lieferung-Switch
  const handleSwitchChange = (name: string, checked: boolean) => {
    console.log(`Switch ${name} geändert zu: ${checked} (Typ: ${typeof checked})`);
    
    // Explizit als Boolean umwandeln zur Sicherheit
    const boolValue = checked === true;
    console.log(`Umgewandelter Boolean-Wert: ${boolValue} (Typ: ${typeof boolValue})`);
    
    // Aktualisiere das Formular mit dem expliziten Boolean-Wert
    setFormData(prev => {
      const updatedData: typeof prev = { ...prev };
      if (name === 'delivery') {
        updatedData.delivery = boolValue;
      } else {
        (updatedData as any)[name] = boolValue;
      }
      console.log(`Neuer Formular-Status für ${name}:`, (updatedData as any)[name], `(Typ: ${typeof (updatedData as any)[name]})`);
      return updatedData;
    });
    
    // Wenn Lieferung aktiviert wird und es keine aktiven Lieferzeiten gibt,
    // standardmäßig werktags aktivieren
    if (name === "delivery" && boolValue && !deliveryHours.some(h => h.isOpen)) {
      // Standard-Werktags-Zeiten setzen (Mo-Fr 12-21 Uhr)
      console.log("Aktiviere Standard-Lieferzeiten für Werktage");
      const updatedHours = [...deliveryHours];
      
      // Wochentage 1-5 (Mo-Fr) auf aktiv setzen
      for (let i = 0; i < 5; i++) {
        updatedHours[i] = {
          ...updatedHours[i],
          isOpen: true,
          start: "12:00",
          end: "21:00"
        };
      }
      
      setDeliveryHours(updatedHours);
      
      // Signal, dass sich der Formularstatus geändert hat
      onDirtyChange(true);
    } else if (name === "delivery" && !boolValue) {
      // Wenn Lieferung deaktiviert wird, zeige eine Warnung, dass Lieferzeiten verloren gehen
      if (deliveryHours.some(h => h.isOpen)) {
        toast({
          title: "Lieferzeiten werden deaktiviert",
          description: "Bei Deaktivierung der Lieferung werden alle eingegebenen Lieferzeiten beim Speichern entfernt.",
          variant: "default"
        });
      }
      
      // Signal, dass sich der Formularstatus geändert hat
      onDirtyChange(true);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleNumberChange = (name: string, value: number | string) => {
    setFormData(prev => ({ ...prev, [name]: Number(value) }));
  };

  // Handler für Änderungen an Lieferzeiten
  const handleHourChange = (index: number, field: keyof DeliveryHour, value: any) => {
    const newHours = [...deliveryHours];
    newHours[index] = {
      ...newHours[index],
      [field]: value
    };
    
    setDeliveryHours(newHours);
  };

  // Standard-Lieferzeiten anwenden
  const applyDefaultHours = () => {
    const defaultHours = deliveryHours.map(hour => ({
      ...hour,
      isOpen: true,
      start: "12:00",
      end: "21:00"
    }));
    
    setDeliveryHours(defaultHours);
  };

  // Werktagslieferzeiten (Mo-Fr) und Wochenendlieferzeiten (Sa-So) anwenden
  const applyWeekdayWeekend = () => {
    const weekdaySettings = {
      isOpen: true,
      start: "12:00",
      end: "20:00"
    };
    
    const weekendSettings = {
      isOpen: true,
      start: "12:00",
      end: "21:00"
    };
    
    const newHours = deliveryHours.map((hour, index) => ({
      ...hour,
      ...(index < 5 ? weekdaySettings : weekendSettings)
    }));
    
    setDeliveryHours(newHours);
  };

  // Kopiere Lieferzeiten von den Öffnungszeiten
  const copyFromOpeningHours = () => {
    if (!restaurant?.openingHours || restaurant.openingHours.length === 0) {
      toast({
        title: "Keine Öffnungszeiten",
        description: "Es sind keine Öffnungszeiten vorhanden, die kopiert werden könnten.",
        variant: "destructive"
      });
      return;
    }

    // Erstelle Map der Öffnungszeiten nach Tag
    const openingHoursByDay = new Map<number, OpeningHour>();
    restaurant.openingHours.forEach(hour => {
      const day = hour.weekday || hour.day || 0;
      if (day > 0) {
        openingHoursByDay.set(day, hour);
      }
    });

    // Kopiere Öffnungszeiten zu Lieferzeiten
    const newHours = deliveryHours.map(hour => {
      const openingHour = openingHoursByDay.get(hour.day);
      
      if (openingHour) {
        return {
          ...hour,
          isOpen: true,
          start: openingHour.openStart || openingHour.opening || hour.start,
          end: openingHour.openEnd || openingHour.closing || hour.end
        };
      } else {
        return hour;
      }
    });
    
    setDeliveryHours(newHours);
  };

  // Alle Tage auf "Geschlossen" setzen
  const closeAllDays = () => {
    const newHours = deliveryHours.map(hour => ({
      ...hour,
      isOpen: false
    }));
    
    setDeliveryHours(newHours);
  };

  // Submit-Handler für das Formular
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Submit Liefereinstellungen");
    
    try {
      setIsSubmitting(true);
      setError(null);
      
      // Speichere die Liefereinstellungen
      console.log("Sende Lieferstatus:", formData.delivery, `(Typ: ${typeof formData.delivery} )`);
      console.log("Sende Liefereinstellungen:", formData);
      
      // Explizit sicherstellen, dass delivery als Boolean gesendet wird
      const settingsPayload = {
        ...formData,
        delivery: formData.delivery === true, // Explizite Boolean-Konvertierung
      };
      
      // Konvertiere unsere UI-Lieferzeiten ins Backend-Format
      const deliveryHoursPayload = deliveryHours
        .filter(hour => hour.isOpen)
        .map(hour => ({
          weekday: hour.day,
          openStart: hour.start,
          openEnd: hour.end
        }));
      
      // Füge Lieferzeiten zum Payload hinzu
      const fullPayload = {
        ...settingsPayload,
        deliveryHours: deliveryHoursPayload
      };
      
      console.log("Sende Daten an den Server:", fullPayload);
      
      // Optimistisches Update des Cache
      const previousData = queryClient.getQueryData([`/api/restaurants/${restaurant.id}`]);
      
      // Optimistisch den Cache aktualisieren
      queryClient.setQueryData([`/api/restaurants/${restaurant.id}`], (old: any) => ({
        ...old,
        ...settingsPayload,
        deliveryHours: deliveryHoursPayload
      }));
      
      try {
        // Speichere die Einstellungen
        const response = await updateDeliverySettingsMutation.mutateAsync(fullPayload);
        console.log("Liefereinstellungen gespeichert:", response);
        
        // Erfolgsmeldung
        toast({
          title: "Liefereinstellungen aktualisiert",
          description: "Die Liefereinstellungen wurden erfolgreich gespeichert."
        });
        
        // Cache mit den Server-Daten aktualisieren
        queryClient.setQueryData([`/api/restaurants/${restaurant.id}`], (old: any) => ({
          ...old,
          ...response.restaurant,
          deliveryHours: response.deliveryHours || response.data || []
        }));
        
        // P0 FIX: Warte kurz, dann invalidate Query um props zu aktualisieren
        await queryClient.invalidateQueries({ queryKey: [`/api/restaurants/${restaurant.id}`] });
        invalidateRestaurantDetail(queryClient, restaurant.id, { includeSubResources: true });
        invalidateAllRestaurantLists(queryClient);
        
        // Formular als nicht mehr dirty markieren (NACH Query invalidation!)
        // Verwende setTimeout um sicherzustellen dass useEffect nach Props-Update läuft
        setTimeout(() => {
          onDirtyChange(false);
        }, 100);
        
      } catch (error) {
        // Bei Fehler den vorherigen Zustand wiederherstellen
        queryClient.setQueryData([`/api/restaurants/${restaurant.id}`], previousData);
        
        // Fehlermeldung anzeigen
        console.error("Fehler beim Speichern:", error);
        setError(`Fehler beim Speichern: ${(error as Error).message}`);
        toast({
          title: "Fehler beim Speichern",
          description: (error as Error).message,
          variant: "destructive"
        });
      }
      
    } catch (error) {
      console.error("Fehler beim Speichern:", error);
      setError(`Fehler beim Speichern: ${(error as Error).message}`);
      toast({
        title: "Fehler beim Speichern",
        description: (error as Error).message,
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Anzahl der geöffneten Liefertage
  const openDeliveryDays = deliveryHours.filter(h => h.isOpen).length;

  return (
    <form id="delivery-form" onSubmit={handleSubmit}>
      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Card className="overflow-hidden border-slate-200/60 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Lieferservice</CardTitle>
          <CardDescription className="text-xs">Konfigurieren Sie Lieferoptionen und -zeiten</CardDescription>
        </CardHeader>
        <CardContent>
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 bg-slate-100">
          <TabsTrigger value="settings">
            <Truck className="mr-2 h-4 w-4" />
            Liefereinstellungen
          </TabsTrigger>
          <TabsTrigger value="hours">
            <Clock className="mr-2 h-4 w-4" />
            Lieferzeiten
          </TabsTrigger>
        </TabsList>

        {/* Liefereinstellungen */}
        <TabsContent value="settings" className="space-y-6">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-1">Allgemeine Liefereinstellungen</h3>
              <p className="text-sm text-gray-500 mb-6">
                Konfigurieren Sie die Liefer- und Bestellungsoptionen für das Restaurant
              </p>
            </div>
            <div className="space-y-6">
              {/* Lieferung aktivieren */}
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="delivery">Lieferung anbieten</Label>
                  <p className="text-sm text-gray-500">
                    Aktivieren Sie die Lieferfunktion für dieses Restaurant
                  </p>
                </div>
                <Switch
                  id="delivery"
                  checked={formData.delivery}
                  onCheckedChange={(checked) => handleSwitchChange("delivery", checked)}
                />
              </div>

              {/* P0 Fix: Saubere Disabled States mit Callout */}
              {!formData.delivery && (
                <Alert className="mb-6 bg-gray-50 border-gray-200">
                  <AlertCircle className="h-4 w-4 text-gray-500" />
                  <AlertDescription className="text-sm text-gray-600">
                    Aktivieren Sie die Lieferung, um Lieferzeiten und Gebühren zu konfigurieren.
                  </AlertDescription>
                </Alert>
              )}

              <div className={formData.delivery ? "space-y-6" : "space-y-6 opacity-50"}>
                {/* Mindestbestellwert */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="minimumOrderValue" className={formData.delivery ? "" : "text-gray-400"}>
                      Mindestbestellwert (€)
                    </Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="minimumOrderValue"
                        name="minimumOrderValue"
                        type="number"
                        min="0"
                        step="0.50"
                        value={formData.minimumOrderValue}
                        onChange={(e) => handleNumberChange("minimumOrderValue", e.target.value)}
                        disabled={!formData.delivery}
                        className={formData.delivery ? "" : "bg-gray-50 cursor-not-allowed"}
                      />
                      <span className="text-lg">€</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Der Mindestbestellwert für die Lieferung
                    </p>
                  </div>

                  {/* Liefergebühr */}
                  <div>
                    <Label htmlFor="deliveryFee">Liefergebühr (€)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="deliveryFee"
                        name="deliveryFee"
                        type="number"
                        min="0"
                        step="0.50"
                        value={formData.deliveryFee}
                        onChange={(e) => handleNumberChange("deliveryFee", e.target.value)}
                        disabled={!formData.delivery}
                        className={formData.delivery ? "" : "bg-gray-50 cursor-not-allowed"}
                      />
                      <span className="text-lg">€</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Die Standard-Liefergebühr für Bestellungen
                    </p>
                  </div>

                  {/* Kostenlose Lieferung ab */}
                  <div>
                    <Label htmlFor="freeDeliveryThreshold">Kostenlose Lieferung ab (€)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        id="freeDeliveryThreshold"
                        name="freeDeliveryThreshold"
                        type="number"
                        min="0"
                        step="0.50"
                        value={formData.freeDeliveryThreshold}
                        onChange={(e) => handleNumberChange("freeDeliveryThreshold", e.target.value)}
                        disabled={!formData.delivery}
                        className={formData.delivery ? "" : "bg-gray-50 cursor-not-allowed"}
                      />
                      <span className="text-lg">€</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Ab diesem Bestellwert ist die Lieferung kostenlos
                    </p>
                  </div>

                  {/* Lieferzeit */}
                  <div>
                    <Label htmlFor="deliveryEstimate">Geschätzte Lieferzeit (Minuten)</Label>
                    <Input
                      id="deliveryEstimate"
                      name="deliveryEstimate"
                      value={formData.deliveryEstimate}
                      onChange={handleInputChange}
                      placeholder="z.B. 30-45"
                      className={`mt-1 ${formData.delivery ? "" : "bg-gray-50 cursor-not-allowed"}`}
                      disabled={!formData.delivery}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Die geschätzte Zeit für die Lieferung (z.B. "30-45" oder "ca. 30")
                    </p>
                  </div>

                  {/* Maximaler Lieferradius */}
                  <div className="col-span-1 md:col-span-2">
                    <div className="flex justify-between">
                      <Label htmlFor="maximumDeliveryRadius">Maximaler Lieferradius (km)</Label>
                      <span className="text-sm font-medium">{formData.maximumDeliveryRadius} km</span>
                    </div>
                    <Slider
                      id="maximumDeliveryRadius"
                      min={1}
                      max={20}
                      step={0.5}
                      value={[formData.maximumDeliveryRadius]}
                      onValueChange={(value) => handleNumberChange("maximumDeliveryRadius", value[0])}
                      className={`mt-2 ${formData.delivery ? "" : "opacity-50"}`}
                      disabled={!formData.delivery}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Die maximale Entfernung, bis zu der geliefert wird
                    </p>
                    
                    {/* Karte zur Visualisierung des Lieferradius */}
                    <div className="mt-4 border rounded-lg overflow-hidden h-[200px] bg-gray-100">
                      <div className="p-4 flex items-center justify-center h-full">
                        <p className="text-gray-500 text-sm text-center">
                          Karte mit Lieferradius (in Entwicklung)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Lieferhinweise */}
                  <div className="col-span-1 md:col-span-2">
                    <Label htmlFor="deliveryInstructions">Lieferhinweise</Label>
                    <textarea
                      id="deliveryInstructions"
                      name="deliveryInstructions"
                      value={formData.deliveryInstructions}
                      onChange={handleInputChange}
                      placeholder="Hinweise zur Lieferung..."
                      className="w-full p-2 mt-1 border border-gray-300 rounded-md focus:ring-primary focus:border-primary min-h-[100px]"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Besondere Hinweise zur Lieferung, die dem Kunden angezeigt werden
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Lieferzeiten */}
        <TabsContent value="hours" className="space-y-6">
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold mb-1">Lieferzeiten</h3>
                <p className="text-sm text-gray-500">
                  Legen Sie fest, zu welchen Zeiten Lieferungen angeboten werden
                </p>
              </div>
              
              <Badge variant={openDeliveryDays > 0 ? "default" : "secondary"} className="text-xs w-fit">
                {openDeliveryDays} von 7 Tagen geöffnet
              </Badge>
            </div>
            <div>
              <div className={formData.delivery ? "" : "opacity-50 pointer-events-none"}>
                {/* Mobile: Kompakte Secondary Buttons/Chips */}
                <div className="md:hidden flex flex-wrap gap-2 mb-4">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={applyDefaultHours}
                    className="text-xs h-8 px-2"
                  >
                    <Check className="h-3 w-3 mr-1 text-green-600" />
                    Standard
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={applyWeekdayWeekend}
                    className="text-xs h-8 px-2"
                  >
                    <Clock className="h-3 w-3 mr-1" />
                    Werktag/WE
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={copyFromOpeningHours}
                    className="text-xs h-8 px-2"
                  >
                    <Truck className="h-3 w-3 mr-1" />
                    Kopieren
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={closeAllDays}
                    className="text-xs h-8 px-2 bg-red-50 hover:bg-red-100 border-red-200"
                  >
                    <AlertCircle className="h-3 w-3 mr-1 text-red-600" />
                    Schließen
                  </Button>
                </div>

                {/* Desktop: Normale Buttons */}
                <div className="hidden md:flex flex-wrap gap-2 mb-6">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={applyDefaultHours}
                  >
                    <Check className="h-4 w-4 mr-2 text-green-600" />
                    Standard-Lieferzeiten
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={applyWeekdayWeekend}
                  >
                    <Clock className="h-4 w-4 mr-2" />
                    Werktag/Wochenende
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={copyFromOpeningHours}
                  >
                    <Truck className="h-4 w-4 mr-2" />
                    Von Öffnungszeiten kopieren
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={closeAllDays}
                    className="bg-red-50 hover:bg-red-100 border-red-200"
                  >
                    <AlertCircle className="h-4 w-4 mr-2 text-red-600" />
                    Alle schließen
                  </Button>
                </div>
                
                {/* Mobile: Day-Cards statt Tabelle */}
                <div className="md:hidden space-y-3">
                  {deliveryHours.map((hour, index) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-4 ${!hour.isOpen ? 'bg-gray-50' : 'bg-white'}`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900">{WEEKDAYS[index]}</h4>
                        <Switch
                          checked={hour.isOpen}
                          onCheckedChange={(checked) => handleHourChange(index, 'isOpen', checked)}
                        />
                      </div>
                      {hour.isOpen && (
                        <div className="grid grid-cols-2 gap-3 mt-3">
                          <div>
                            <Label className="text-sm text-gray-600 mb-1 block">Von</Label>
                            <Input
                              type="time"
                              value={hour.start}
                              onChange={(e) => handleHourChange(index, 'start', e.target.value)}
                              className="p-2 border border-gray-300 rounded"
                            />
                          </div>
                          <div>
                            <Label className="text-sm text-gray-600 mb-1 block">Bis</Label>
                            <Input
                              type="time"
                              value={hour.end}
                              onChange={(e) => handleHourChange(index, 'end', e.target.value)}
                              className="p-2 border border-gray-300 rounded"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Desktop: Tabelle */}
                <div className="hidden md:block border rounded-lg p-4">
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[600px]">
                      <thead className="bg-gray-50 text-left">
                        <tr>
                          <th className="px-4 py-3 text-sm font-medium text-gray-500">Tag</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-500">Lieferung</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-500">Von</th>
                          <th className="px-4 py-3 text-sm font-medium text-gray-500">Bis</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {deliveryHours.map((hour, index) => (
                          <tr key={index} className={!hour.isOpen ? 'bg-gray-50' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-3 font-medium">
                              {WEEKDAYS[index]}
                            </td>
                            <td className="px-4 py-3">
                              <Switch
                                checked={hour.isOpen}
                                onCheckedChange={(checked) => handleHourChange(index, 'isOpen', checked)}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="time"
                                value={hour.start}
                                onChange={(e) => handleHourChange(index, 'start', e.target.value)}
                                disabled={!hour.isOpen}
                                className={`p-1.5 border rounded ${!hour.isOpen ? 'bg-gray-100 text-gray-400' : 'border-gray-300'}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="time"
                                value={hour.end}
                                onChange={(e) => handleHourChange(index, 'end', e.target.value)}
                                disabled={!hour.isOpen}
                                className={`p-1.5 border rounded ${!hour.isOpen ? 'bg-gray-100 text-gray-400' : 'border-gray-300'}`}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              
              {!formData.delivery && (
                <Alert className="mt-4 bg-amber-50 border-amber-200">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <AlertDescription className="text-amber-700">
                    Aktivieren Sie zuerst die Lieferung in den Einstellungen, um Lieferzeiten zu bearbeiten.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>
        </CardContent>
      </Card>

      {/* Desktop save bar — mobile save is in RestaurantEditPage bottom bar */}
      <div className="hidden md:flex justify-end mt-6">
        <Button 
          type="submit" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Wird gespeichert...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Liefereinstellungen speichern
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default RestaurantFormDelivery; 